import React, { ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  GetProfileIncludeOptions,
  ProfileAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { Page } from '@qualifio/api-client';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';

export const profilesPageSize = 10;

export interface IProfilesContext {
  profiles: Page<ProfileAttributes>;
  setProfiles: (profiles: Page<ProfileAttributes>) => void;
  selectedProfile: ProfileAttributes | undefined;
  currentPage: number;
  setCurrentPage: (pageNumber: number) => void;
  error: any;
  setSelectedProfile: (profile: ProfileAttributes | undefined) => void;
  getProfileById: (profileId: string) => Promise<ProfileAttributes | null>;
  searchProfiles: (
    search: string | undefined,
    pageNumber: number,
  ) => Promise<Page<ProfileAttributes> | null>;
  refreshProfile: () => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  isLoading: IsLoading;
}

export const ProfilesContext = createContext<IProfilesContext>({} as any);

export function useProfiles() {
  return useContext(ProfilesContext);
}

export function ProfilesProvider({ children }: { children: ReactNode }) {
  const { program } = useProgram();
  const { profileApi } = useApi();
  const [error, setError] = useState<any>();
  const [profiles, setProfiles] = useState<Page<ProfileAttributes>>(undefined as any);
  const [selectedProfile, setSelectedProfile] = useState<ProfileAttributes>();
  const [currentPage, setCurrentPage] = useState(0);
  const searchRef = useRef<string | undefined>(undefined);

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const searchProfiles = useCallback(
    async (search: string | undefined, pageNumber?: number) => {
      addLoader('searchProfiles');
      searchRef.current = search;
      if (!!search && search.length < 3) {
        return null;
      } else {
        return profileApi
          .searchProfiles({
            params: {
              pageSize: profilesPageSize,
              search,
              pageNumber,
              programId: program.id,
            },
          })
          .then(page => {
            setProfiles(page);
            setError(undefined);
            return page;
          })
          .catch(error => {
            console.error(error);
            setError(error);
            return null;
          })
          .finally(() => removeLoader('searchProfiles'));
      }
    },
    [program, profileApi, addLoader, removeLoader],
  );

  const getProfileById = useCallback(
    async (profileId: string) => {
      addLoader('getSpecificProfile');

      return profileApi
        .getProfile(profileId, {
          params: {
            include: [
              GetProfileIncludeOptions.currencies,
              GetProfileIncludeOptions.memberships,
              GetProfileIncludeOptions.rewards,
              GetProfileIncludeOptions.identities,
            ],
            programId: program.id,
          },
        })
        .then(profile => {
          setSelectedProfile(profile);
          setError(undefined);
          return profile;
        })
        .catch(error => {
          console.error(error);
          setError(error);
          return null;
        })
        .finally(() => removeLoader('getSpecificProfile'));
    },
    [program, profileApi, addLoader, removeLoader],
  );

  const refreshProfile = useCallback(async () => {
    addLoader('refreshProfile');

    if (selectedProfile) {
      const updatedProfile = await profileApi.getProfile(selectedProfile.id, {
        params: {
          include: [
            GetProfileIncludeOptions.currencies,
            GetProfileIncludeOptions.memberships,
            GetProfileIncludeOptions.rewards,
            GetProfileIncludeOptions.identities,
          ],
          programId: program.id,
        },
      });

      setProfiles(prev => ({
        ...prev,
        data: prev.data.map(profile =>
          profile.id === selectedProfile.id ? updatedProfile : profile,
        ),
      }));
      setSelectedProfile(updatedProfile);
    }

    removeLoader('refreshProfile');
  }, [program, profileApi, addLoader, removeLoader, selectedProfile?.id]);

  const goToPreviousPage = useCallback(async () => {
    const profiles = await searchProfiles(searchRef.current, currentPage - 1);
    if (profiles) {
      setCurrentPage(prev => prev - 1);
      setSelectedProfile(undefined);
    }
  }, [searchProfiles, currentPage]);

  const goToNextPage = useCallback(async () => {
    const profiles = await searchProfiles(searchRef.current, currentPage + 1);
    if (profiles) {
      setCurrentPage(prev => prev + 1);
      setSelectedProfile(undefined);
    }
  }, [searchProfiles, currentPage]);

  const context = {
    profiles,
    setProfiles,
    selectedProfile,
    currentPage,
    setCurrentPage,
    error,
    setSelectedProfile,
    getProfileById,
    searchProfiles,
    refreshProfile,
    goToPreviousPage,
    goToNextPage,
    isLoading,
  };

  return <ProfilesContext.Provider value={context}>{children}</ProfilesContext.Provider>;
}
